import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { AlertCircle, CheckCircle, Moon, Play, Pause, RotateCcw, Timer, Edit, ArrowLeft, SkipForward } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useDevelopTimer } from '@/hooks/useDevelopTimer';
import { DevelopmentRecipe, DevelopmentSession } from '@/types/develop';
import { cn } from '@/lib/utils';
import { useDarkroomMode } from '@/hooks/useDarkroomMode';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

interface DevelopmentTimerProps {
  recipe: DevelopmentRecipe;
  onComplete: (session: Omit<DevelopmentSession, 'id'>) => void;
  onBack: () => void;
  onEditRecipe: () => void;
}
export const DevelopmentTimer: React.FC<DevelopmentTimerProps> = ({
  recipe,
  onComplete,
  onBack,
  onEditRecipe
}) => {
  const {
    t
  } = useTranslation();
  const [notes, setNotes] = useState('');
  const [activeTab, setActiveTab] = useState('notes');
  const [sessionSuccess, setSessionSuccess] = useState(true);
  const {
    darkroomMode,
    toggleDarkroomMode
  } = useDarkroomMode();
  const [autoStartNextStep, setAutoStartNextStep] = useState(false);
  const timer = useDevelopTimer(recipe.steps, autoStartNextStep);

  const handleComplete = () => {
    onComplete({
      recipeId: recipe.id,
      startTime: Date.now() - timer.getTotalElapsedTime() * 1000,
      endTime: Date.now(),
      notes: notes.trim() || undefined,
      success: sessionSuccess
    });
    onBack();
  };

  // Determine if the timer is in the critical phase (developer step)
  const isInDeveloperStep = timer.currentStep?.name.toLowerCase().includes('develop');

  // Calculate agitation markers for the progress bar
  const agitationMarkers = useMemo(() => {
    return timer.getAgitationTimes().map(time => time / (timer.currentStep?.time || 1) * 100);
  }, [timer]);
  
  return <div className={cn('flex flex-col h-full w-full space-y-4 transition-colors duration-300', darkroomMode && 'bg-black text-red-500')}>
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Button variant="ghost" size="icon" onClick={onBack} className={darkroomMode ? 'text-red-500 hover:text-red-400 hover:bg-red-950' : ''}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          
          <h2 className={cn('text-lg font-semibold ml-2 truncate max-w-[150px] sm:max-w-full', darkroomMode && 'text-red-400')}>
            {recipe.name}
          </h2>
        </div>
        
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={toggleDarkroomMode} className={cn(darkroomMode ? 'text-red-500 hover:text-red-400 hover:bg-red-950' : '', 'hidden sm:flex')}>
            <Moon className="h-4 w-4 mr-1" />
            {darkroomMode ? 'Exit Darkroom' : 'Darkroom Mode'}
          </Button>
          
          <Button variant="ghost" size="sm" onClick={toggleDarkroomMode} className={cn(darkroomMode ? 'text-red-500 hover:text-red-400 hover:bg-red-950' : '', 'sm:hidden')}>
            <Moon className="h-4 w-4" />
          </Button>
          
          <Button variant="ghost" size="sm" onClick={onEditRecipe} className={cn(darkroomMode ? 'text-red-500 hover:text-red-400 hover:bg-red-950' : '', 'hidden sm:flex')} disabled={timer.isRunning}>
            <Edit className="h-4 w-4 mr-1" />
            Edit Recipe
          </Button>
          
          <Button variant="ghost" size="sm" onClick={onEditRecipe} className={cn(darkroomMode ? 'text-red-500 hover:text-red-400 hover:bg-red-950' : '', 'sm:hidden')} disabled={timer.isRunning}>
            <Edit className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      <div className={cn('grid gap-4', 'grid-cols-1 md:grid-cols-3')}>
        <div className="md:col-span-2">
          <Card className={cn('w-full h-full', darkroomMode && 'bg-black border-red-900')}>
            <CardHeader className="pb-2">
              <div className="flex justify-between flex-wrap gap-2">
                <CardTitle className={cn('text-xl font-bold truncate max-w-[200px] sm:max-w-full', darkroomMode && 'text-red-400')}>
                  {timer.currentStep?.name || 'Complete'}
                </CardTitle>
                
                <div className={cn('text-lg font-mono', darkroomMode && 'text-red-500', timer.agitationDue && 'animate-pulse text-yellow-500')}>
                  {timer.stepComplete ? <span className={cn("text-green-500", darkroomMode && "text-red-400")}>
                      Complete
                    </span> : <>
                      {Math.floor(timer.elapsedTime / 60)}:
                      {String(Math.floor(timer.elapsedTime % 60)).padStart(2, '0')}
                      /
                      {Math.floor(timer.currentStep?.time / 60)}:
                      {String(Math.floor(timer.currentStep?.time % 60)).padStart(2, '0')}
                    </>}
                </div>
              </div>
                          
              <Progress value={timer.elapsedTime / (timer.currentStep?.time || 1) * 100} className={cn('h-2 mt-1', darkroomMode && 'bg-red-950')} isDevelopTimer={true} markers={agitationMarkers} />
              
              <div className="flex items-center justify-between mt-3">
                <div className="flex items-center space-x-2">
                  <Switch id="auto-next" checked={autoStartNextStep} onCheckedChange={setAutoStartNextStep} className={darkroomMode ? 'darkroom-mode-switch' : ''} />
                  <Label htmlFor="auto-next" className={cn('text-sm cursor-pointer', darkroomMode && 'text-red-400')}>
                    Auto-start next step
                  </Label>
                </div>
              </div>
                          
              <div className={cn('text-sm text-muted-foreground mt-2', darkroomMode && 'text-red-700')}>
                {timer.currentStep?.agitationType !== 'none' && <div className="flex justify-between flex-wrap gap-1">
                    <span>Agitation:</span>
                    <span>
                      {timer.isInitialAgitationPhase ? 'Continuous for first 30 seconds' : timer.getAgitationInstructionText()}
                    </span>
                  </div>}
                
                
                {isInDeveloperStep && <div className={cn('mt-2 p-2 rounded-md bg-red-100 text-red-800', darkroomMode && 'bg-red-950 text-red-400')}>
                    <AlertCircle className="inline-block mr-1 h-4 w-4" />
                    Critical Phase: Developer step - timing is crucial!
                  </div>}
              </div>
            </CardHeader>
                    
            <CardFooter className="pt-0 flex-col">
              <div className="flex flex-wrap gap-2 w-full justify-center mt-4">
                {timer.allComplete ? <>
                    <Button onClick={handleComplete} className={cn('flex-1', darkroomMode && 'bg-green-800 hover:bg-green-700 text-white')}>
                      <CheckCircle className="mr-1 h-4 w-4" />
                      Complete
                    </Button>
                                    
                    <Button variant="outline" onClick={timer.resetTimer} className={cn('flex-1', darkroomMode && 'border-red-900 text-red-500 hover:bg-red-950')}>
                      <RotateCcw className="mr-1 h-4 w-4" />
                      Start Over
                    </Button>
                  </> : <>
                    {timer.isRunning ? <Button onClick={timer.pauseTimer} variant="outline" className={cn('flex-1', darkroomMode && 'border-red-900 text-red-500 hover:bg-red-950')}>
                        <Pause className="mr-1 h-4 w-4" />
                        Pause
                      </Button> : <Button onClick={timer.startTimer} className={cn('flex-1', darkroomMode && 'bg-red-800 hover:bg-red-700 text-white')}>
                        <Play className="mr-1 h-4 w-4" />
                        {timer.elapsedTime > 0 ? 'Resume' : 'Start'}
                      </Button>}
                    
                    {timer.stepComplete ? <Button onClick={timer.nextStep} className={cn('flex-1', darkroomMode ? 'bg-black text-red-500 border border-red-800 hover:bg-red-950' : 'bg-red-600 hover:bg-red-700 text-white')}>
                        Next Step
                      </Button> : <>
                        <Button variant="outline" onClick={timer.resetStep} disabled={timer.elapsedTime === 0} className={cn('flex-1 sm:flex-none', darkroomMode && 'border-red-900 text-red-500 hover:bg-red-950')}>
                          <RotateCcw className="mr-1 h-4 w-4" />
                          <span className="hidden sm:inline">Reset Step</span>
                          <span className="sm:hidden">Reset</span>
                        </Button>
                        
                        <Button variant="outline" onClick={timer.skipStep} className={cn('flex-1 sm:flex-none', darkroomMode && 'border-red-900 text-red-500 hover:bg-red-950')}>
                          <SkipForward className="mr-1 h-4 w-4" />
                          <span className="hidden sm:inline">Skip Step</span>
                          <span className="sm:hidden">Skip</span>
                        </Button>
                      </>}
                  </>}
              </div>
              
              {/* Agitation notification with enhanced pulsing effect */}
              {timer.agitationDue && timer.isRunning && <div className={cn('mt-4 w-full text-center rounded-lg py-3 transition-all duration-500 animate-agitate-pulse', darkroomMode ? 'bg-red-950/70 text-white border border-red-800 shadow-[0_0_8px_rgba(220,38,38,0.3)]' : 'bg-yellow-300 text-yellow-900 border border-yellow-400 shadow-sm')}>
                  <div className={cn('text-3xl font-bold tracking-wider', darkroomMode ? 'text-red-300' : 'text-yellow-900')}>
                    AGITATE
                  </div>
                  <div className="text-sm mt-1 opacity-90">
                    {timer.isInitialAgitationPhase ? "Continuously for first 30 seconds" : `For ${timer.currentStep?.agitationDuration} seconds`}
                  </div>
                </div>}
            </CardFooter>
          </Card>
        </div>
                
        <div>
          <Card className={cn('w-full', darkroomMode && 'bg-black border-red-900')}>
            <CardHeader className="pb-2">
              <CardTitle className={cn('text-lg', darkroomMode && 'text-red-400')}>
                Steps
              </CardTitle>
            </CardHeader>
                    
            <CardContent>
              <div className={cn('space-y-2', darkroomMode && 'text-red-500')}>
                {timer.steps.length === 0 ? <div className="text-center py-6 text-muted-foreground">
                    No steps found in this recipe.
                  </div> : timer.steps.map((step, index) => <div key={step.id} className={cn('px-3 py-2 rounded-md text-sm', index === timer.currentStepIndex && 'bg-primary/10 font-medium', index < timer.currentStepIndex && 'line-through opacity-50', darkroomMode && 'text-red-500', darkroomMode && index === timer.currentStepIndex && 'bg-red-950 text-red-400')}>
                      {index + 1}. {step.name} ({Math.floor(step.time / 60)}:{String(step.time % 60).padStart(2, '0')})
                    </div>)}
              </div>
            </CardContent>
                    
            <CardFooter>
              <div className={cn('text-sm text-muted-foreground w-full', darkroomMode && 'text-red-700')}>
                <div className="flex justify-between">
                  <span>Total Time:</span>
                  <span>
                    {Math.floor(timer.steps.reduce((acc, step) => acc + step.time, 0) / 60)}:
                    {String(timer.steps.reduce((acc, step) => acc + step.time, 0) % 60).padStart(2, '0')}
                  </span>
                </div>
                
                {recipe.temperature && <div className="flex justify-between mt-1">
                    <span>Temperature:</span>
                    <span>{recipe.temperature}°C</span>
                  </div>}
              </div>
            </CardFooter>
          </Card>
        </div>
      </div>
            
      <Card className={cn('w-full', darkroomMode && 'bg-black border-red-900')}>
        <CardHeader className="pb-2">
          <Tabs defaultValue="notes" value={activeTab} onValueChange={setActiveTab}>
            <TabsList className={cn('w-full', darkroomMode && 'bg-red-950')}>
              <TabsTrigger value="notes" className={cn('flex-1', darkroomMode && 'data-[state=active]:bg-black data-[state=active]:text-red-400')}>
                Session Notes
              </TabsTrigger>
              <TabsTrigger value="result" className={cn('flex-1', darkroomMode && 'data-[state=active]:bg-black data-[state=active]:text-red-400')}>
                Development Result
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="notes" className="m-0">
              <Textarea placeholder="Add notes about this development session..." value={notes} onChange={e => setNotes(e.target.value)} className={cn('min-h-20', darkroomMode && 'bg-black border-red-900 text-red-500 placeholder:text-red-800')} />
            </TabsContent>
                    
            <TabsContent value="result" className="m-0">
              <div className="space-y-4">
                <div className="flex flex-col gap-2">
                  <div className={cn('text-sm font-medium', darkroomMode && 'text-red-500')}>
                    How did your film turn out?
                  </div>
                            
                  <div className="flex gap-2">
                    <Button variant={sessionSuccess ? "default" : "outline"} size="sm" onClick={() => setSessionSuccess(true)} className={cn(sessionSuccess && darkroomMode && 'bg-red-900 hover:bg-red-800 text-red-100', !sessionSuccess && darkroomMode && 'border-red-900 text-red-500 hover:bg-red-950')}>
                      <CheckCircle className="mr-1 h-4 w-4" />
                      Success
                    </Button>
                                
                    <Button variant={!sessionSuccess ? "default" : "outline"} size="sm" onClick={() => setSessionSuccess(false)} className={cn(!sessionSuccess && darkroomMode && 'bg-red-800 hover:bg-red-700 text-white', sessionSuccess && darkroomMode && 'border-red-900 text-red-500 hover:bg-red-950')}>
                      <AlertCircle className="mr-1 h-4 w-4" />
                      Issues
                    </Button>
                  </div>
                </div>
                        
                <Textarea placeholder={sessionSuccess ? "What worked well? Any observations about the negatives?" : "What issues did you encounter? What would you change next time?"} value={notes} onChange={e => setNotes(e.target.value)} className={cn('min-h-20', darkroomMode && 'bg-black border-red-900 text-red-500 placeholder:text-red-800')} />
              </div>
            </TabsContent>
          </Tabs>
        </CardHeader>
      </Card>
    </div>;
};
